import React, { useState, useMemo, useEffect } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { ArrowLeft, RefreshCw, Send, Check, Lock, Briefcase, AlertTriangle, ChevronRight, Gift, Clock } from "lucide-react";
import {
  QUIZPOST_META,
  QUIZPOST_QUESTIONS,
  scoreConditionsPost,
  overallSeverityPost,
} from "../quizPosturalData";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

const COUPON_DURATION_SECONDS = 3 * 60;
const generateCouponCode = () => {
  const chars = "ABCDEFGHJKLMNPQRSTUVWXYZ23456789";
  let code = "POST-";
  for (let i = 0; i < 4; i++) code += chars[Math.floor(Math.random() * chars.length)];
  return code;
};

const useCountdown = (totalSeconds) => {
  const [expiresAt] = useState(() => Date.now() + totalSeconds * 1000);
  const [remaining, setRemaining] = useState(totalSeconds);
  useEffect(() => {
    const id = setInterval(() => {
      const r = Math.max(0, Math.floor((expiresAt - Date.now()) / 1000));
      setRemaining(r);
      if (r === 0) clearInterval(id);
    }, 500);
    return () => clearInterval(id);
  }, [expiresAt]);
  return {
    remaining,
    mm: String(Math.floor(remaining / 60)).padStart(2, "0"),
    ss: String(remaining % 60).padStart(2, "0"),
    expired: remaining === 0,
  };
};

const COLOR_STYLES = {
  green: { bg: "bg-green-50", border: "border-green-200", text: "text-green-700", btn: "bg-green-600 hover:bg-green-700" },
  yellow: { bg: "bg-amber-50", border: "border-amber-200", text: "text-amber-800", btn: "bg-amber-600 hover:bg-amber-700" },
  red: { bg: "bg-rose-50", border: "border-rose-200", text: "text-rose-700", btn: "bg-rose-600 hover:bg-rose-700" },
};

const SEVERITY_BADGE = {
  high: { label: "Atención prioritaria", style: "bg-rose-100 text-rose-700 border-rose-200" },
  mid: { label: "Tratable con terapia", style: "bg-amber-100 text-amber-800 border-amber-200" },
  low: { label: "Mantenimiento", style: "bg-blue-100 text-blue-700 border-blue-200" },
};

const Intro = ({ onStart }) => (
  <section className="relative max-w-5xl mx-auto px-6 lg:px-8 py-12 sm:py-16 overflow-hidden" data-testid="quizpost-intro">
    <div className="pointer-events-none absolute -top-12 -left-12 w-72 h-72 rounded-full bg-blue-400/25 blur-3xl animate-pulse" />
    <div className="pointer-events-none absolute -bottom-12 -right-12 w-80 h-80 rounded-full bg-amber-300/30 blur-3xl animate-pulse" style={{ animationDelay: "1s" }} />

    <div className="relative grid lg:grid-cols-5 gap-10 items-center">
      <div className="lg:col-span-3 text-center lg:text-left">
        <div className="inline-flex items-center gap-2 rounded-full bg-white shadow-md border border-blue-200 pl-2 pr-4 py-1.5">
          <span className="inline-flex h-7 w-7 items-center justify-center rounded-full bg-gradient-to-br from-blue-500 to-indigo-600 text-white">
            <Briefcase className="h-4 w-4" />
          </span>
          <span className="text-[11px] font-bold tracking-[0.2em] uppercase text-blue-800">
            Diagnóstico postural · oficinistas
          </span>
        </div>

        <h1 className="mt-6 font-display text-5xl sm:text-6xl lg:text-7xl font-black tracking-tighter text-slate-900 leading-[0.95]">
          ¿Tu trabajo<br />
          te está <span className="bg-gradient-to-r from-blue-600 via-indigo-500 to-rose-500 bg-clip-text text-transparent">rompiendo</span><br />
          el cuerpo?
        </h1>

        <p className="mt-6 text-base sm:text-lg text-slate-600 leading-relaxed max-w-xl mx-auto lg:mx-0">
          Pasar <b>8+ horas frente a la pantalla</b> moldea tu cuerpo en silencio. Detectamos en <b>2 minutos</b> los <b>10 padecimientos</b> más comunes en oficinistas: lumbalgia, dolor de cuello, túnel carpiano y más.
        </p>

        <div className="mt-7 grid grid-cols-3 gap-3 sm:gap-5 max-w-md mx-auto lg:mx-0">
          {[
            { num: "10", label: "Padecimientos" },
            { num: "10", label: "Preguntas" },
            { num: "2 min", label: "Lo que tarda" },
          ].map((s) => (
            <div key={s.label} className="rounded-2xl bg-white shadow-sm border border-slate-100 px-3 py-4 text-center">
              <p className="font-display text-2xl sm:text-3xl font-black text-blue-600">{s.num}</p>
              <p className="mt-0.5 text-[10px] sm:text-xs font-semibold text-slate-500 uppercase tracking-wider">{s.label}</p>
            </div>
          ))}
        </div>

        <div className="mt-9">
          <button
            onClick={onStart}
            className="group inline-flex items-center gap-3 rounded-full bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-700 hover:to-indigo-700 text-white px-8 py-4 text-base font-bold shadow-xl shadow-blue-300/40 transition-all hover:-translate-y-0.5"
            data-testid="quizpost-start"
          >
            Iniciar mi diagnóstico
            <ChevronRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
          </button>
          <p className="mt-3 text-[11px] text-slate-400 italic">
            Orientativa · No reemplaza diagnóstico profesional
          </p>
        </div>
      </div>

      <div className="lg:col-span-2 relative">
        <div className="relative aspect-[4/5] max-w-sm mx-auto">
          <div className="absolute inset-0 rounded-[2rem] bg-gradient-to-br from-blue-500 via-indigo-500 to-amber-400 rotate-3" />
          <img
            src="/img/quiz/postural-cover.png"
            alt="Profesional oficinista con dolor postural en el escritorio"
            className="relative w-full h-full object-cover rounded-[2rem] shadow-2xl shadow-blue-300/40 -rotate-2"
          />
          <div className="absolute -top-3 -left-3 sm:-left-6 rounded-2xl bg-white shadow-xl border border-blue-100 px-4 py-3 max-w-[180px] animate-bounce-slow">
            <div className="flex items-center gap-2">
              <div className="h-8 w-8 rounded-full bg-blue-100 inline-flex items-center justify-center">
                <Check className="h-4 w-4 text-blue-700" />
              </div>
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700">Postura</p>
                <p className="text-xs text-slate-700 font-semibold leading-tight">tu cuerpo te habla</p>
              </div>
            </div>
          </div>
          <div className="absolute -bottom-3 -right-3 sm:-right-6 rounded-2xl bg-white shadow-xl border border-amber-100 px-4 py-3 max-w-[220px]">
            <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700 mb-0.5">Al terminar</p>
            <p className="text-sm text-slate-900 font-bold leading-tight">2x1 ZONAS</p>
            <p className="text-[10px] text-slate-500">2 zonas al precio de 1</p>
          </div>
        </div>
      </div>
    </div>
  </section>
);

const customAnimationsCSS = `
  @keyframes bounce-slow { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-6px)} }
  .animate-bounce-slow { animation: bounce-slow 3s ease-in-out infinite; }
`;

const Question = ({ q, idx, total, onAnswer, onBack }) => {
  const progress = Math.round(((idx + 1) / total) * 100);
  return (
    <section className="max-w-2xl mx-auto px-6 lg:px-8 py-14" data-testid="quizpost-question">
      <div className="flex items-center justify-between mb-5">
        <button onClick={onBack} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900">
          <ArrowLeft className="h-4 w-4" /> Volver
        </button>
        <span className="text-xs font-bold tracking-widest uppercase text-blue-700">Diagnóstico postural</span>
      </div>
      <div className="w-full h-1.5 rounded-full bg-slate-100 overflow-hidden mb-8">
        <div className="h-full bg-gradient-to-r from-blue-500 to-indigo-600 transition-all duration-300" style={{ width: `${progress}%` }} />
      </div>
      <p className="text-xs font-semibold tracking-widest uppercase text-slate-400 mb-3">Pregunta {idx + 1} de {total}</p>
      <h2 className="font-display text-2xl sm:text-3xl font-bold text-slate-900 leading-tight">{q.text}</h2>
      <div className="mt-10 grid grid-cols-2 gap-4">
        <button onClick={() => onAnswer(true)} className="rounded-2xl border-2 border-slate-200 bg-white py-6 font-display font-bold text-lg text-slate-900 hover:border-blue-500 hover:bg-blue-50 transition-all" data-testid="quizpost-yes">Sí, me pasa</button>
        <button onClick={() => onAnswer(false)} className="rounded-2xl border-2 border-slate-200 bg-white py-6 font-display font-bold text-lg text-slate-900 hover:border-rose-400 hover:bg-rose-50 transition-all" data-testid="quizpost-no">No</button>
      </div>
    </section>
  );
};

const Result = ({ ranked, severity, answers, onRestart }) => {
  const palette = COLOR_STYLES[severity];
  const top = ranked.slice(0, 3);
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [phone, setPhone] = useState("");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [err, setErr] = useState("");
  const coupon = useMemo(() => generateCouponCode(), []);
  const countdown = useCountdown(COUPON_DURATION_SECONDS);
  const couponActive = !countdown.expired;

  const summaryText = useMemo(() => top.length === 0 ? "Sin señales relevantes — tu postura va muy bien." : top.map((c) => `• ${c.name}`).join("\n"), [top]);

  const waLink = useMemo(() => {
    let msg = `Hola, hice el "Diagnóstico Postural Express" en TerafiQuiro y mis posibles padecimientos son:\n${summaryText}\n\nQuiero agendar valoración con el Fisio Omar.`;
    if (couponActive) msg += `\n\n🎁 Tengo el cupón ${coupon} (2 zonas al precio de 1 en mi primera consulta).`;
    return `https://wa.me/${QUIZPOST_META.therapist.phoneWa}?text=${encodeURIComponent(msg)}`;
  }, [summaryText, coupon, couponActive]);

  const submitLead = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      await axios.post(`${API}/quiz/submit`, {
        quiz_id: "postural-oficinistas", quiz_title: QUIZPOST_META.title,
        score: top.length, result_color: severity,
        result_label: top.length > 0 ? top.map((c) => c.short).join(" · ") : "Sin hallazgos",
        recommended_service: QUIZPOST_META.therapist.service,
        recommended_therapist: QUIZPOST_META.therapist.name,
        answers: answers.map(Boolean),
        name: name.trim(), email: email.trim(), phone: phone.trim(),
        coupon_code: couponActive ? coupon : "",
      });
      setSaved(true);
    } catch { setErr("No pudimos guardar tus datos. Inténtalo de nuevo."); }
    finally { setSaving(false); }
  };

  return (
    <section className="max-w-3xl mx-auto px-6 lg:px-8 py-14" data-testid="quizpost-result">
      <div className="text-center">
        <span className={`inline-flex items-center gap-1.5 rounded-full ${palette.bg} ${palette.text} border ${palette.border} px-4 py-1.5 text-xs font-bold tracking-widest uppercase`}>
          <AlertTriangle className="h-3.5 w-3.5" /> Resultado de tu diagnóstico
        </span>
        <h1 className="mt-5 font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
          {top.length === 0 ? "Sin señales relevantes detectadas" : `Detectamos ${top.length} ${top.length === 1 ? "posible padecimiento" : "posibles padecimientos"}`}
        </h1>
        <p className="mt-3 text-base text-slate-600 max-w-xl mx-auto">
          {top.length === 0 ? "Tu postura va bien. Una valoración preventiva cada 6 meses te mantiene así." : "Estas son las condiciones más probables según tus respuestas. Una valoración clínica las confirma."}
        </p>
      </div>

      {couponActive ? (
        <div className="mt-8 rounded-3xl bg-gradient-to-br from-amber-500 via-orange-500 to-rose-500 p-1.5 shadow-2xl shadow-orange-200/60" data-testid="quizpost-coupon">
          <div className="rounded-[22px] bg-white px-6 py-8 sm:px-10 sm:py-10 text-center">
            <div className="inline-flex h-16 w-16 items-center justify-center rounded-2xl bg-gradient-to-br from-amber-500 to-rose-500 text-white shadow-lg mb-4">
              <Gift className="h-8 w-8" />
            </div>
            <p className="text-[11px] font-bold tracking-[0.25em] uppercase text-orange-600 mb-2">Oferta flash · solo para ti</p>
            <p className="font-display text-3xl sm:text-5xl font-black text-slate-900 leading-none tracking-tight">2 zonas <span className="text-orange-600">al precio de 1</span></p>
            <p className="mt-3 font-display text-base sm:text-lg font-bold text-slate-900">Atendemos 2 zonas de dolor en tu primera consulta con el Fisio Omar</p>
            <p className="mt-1 text-sm text-slate-500">Si agendas <b>en este momento</b></p>
            <div className="mt-6 inline-flex items-center gap-3 rounded-2xl bg-slate-900 px-5 py-3 text-white">
              <Clock className="h-5 w-5 animate-pulse text-amber-400" />
              <span className="font-mono text-2xl font-bold tracking-wider">{countdown.mm}:{countdown.ss}</span>
              <span className="text-xs text-slate-300 font-semibold">restantes</span>
            </div>
            <div className="mt-6 rounded-2xl bg-amber-50 border-2 border-dashed border-amber-300 px-5 py-4 inline-flex flex-wrap items-center justify-center gap-3 max-w-md mx-auto">
              <div>
                <p className="text-[10px] font-bold uppercase tracking-widest text-amber-700">Tu código</p>
                <p className="font-mono text-2xl sm:text-3xl font-bold text-amber-900 tracking-wider">{coupon}</p>
              </div>
              <button type="button" onClick={() => navigator.clipboard?.writeText(coupon)} className="rounded-full bg-amber-900 text-white px-4 py-2 text-xs font-bold hover:bg-amber-800">Copiar</button>
            </div>
            <div className="mt-7 flex flex-col sm:flex-row gap-3 justify-center">
              <a href={waLink} target="_blank" rel="noopener noreferrer" className="inline-flex items-center justify-center gap-2 rounded-full bg-[#25D366] hover:bg-[#1ebe57] text-white px-7 py-3.5 text-sm font-bold shadow-lg shadow-green-300/40"><Send className="h-4 w-4" /> Agendar por WhatsApp</a>
              <Link to="/#booking-fisio" className="inline-flex items-center justify-center gap-2 rounded-full bg-slate-900 hover:bg-slate-800 text-white px-7 py-3.5 text-sm font-bold">Ver calendario</Link>
            </div>
            <p className="mt-5 text-xs text-slate-500">Menciona el código al agendar. Válido solo en los próximos 3 minutos.</p>
          </div>
        </div>
      ) : (
        <div className="mt-8 rounded-3xl bg-slate-100 px-6 py-5 text-center">
          <p className="text-sm font-semibold text-slate-500">El cupón flash ha expirado. Aún así, contáctanos — siempre tenemos algo especial para nuevos pacientes.</p>
        </div>
      )}

      {top.length > 0 && (
        <div className="mt-8 space-y-3">
          {top.map((c, idx) => {
            const sev = SEVERITY_BADGE[c.severity] || SEVERITY_BADGE.mid;
            return (
              <div key={c.key} className="rounded-2xl bg-white shadow-sm border border-slate-100 p-5 sm:p-6">
                <div className="flex items-start justify-between gap-4 flex-wrap">
                  <div className="flex-1 min-w-0">
                    <p className="text-xs font-bold uppercase tracking-widest text-slate-400">#{idx + 1} más probable</p>
                    <h3 className="mt-1 font-display text-xl font-bold text-slate-900">{c.name}</h3>
                  </div>
                  <span className={`inline-flex rounded-full ${sev.style} border px-2.5 py-1 text-[10px] font-bold uppercase tracking-widest`}>{sev.label}</span>
                </div>
                <p className="mt-3 text-sm text-slate-600 leading-relaxed">{c.description}</p>
                <div className="mt-4 rounded-xl bg-blue-50/60 border border-blue-100 p-4">
                  <p className="text-[10px] font-bold uppercase tracking-widest text-blue-700 mb-1">Cómo lo tratamos</p>
                  <p className="text-sm text-slate-700 leading-relaxed">{c.treatment}</p>
                </div>
              </div>
            );
          })}
        </div>
      )}

      {!saved && (
        <form onSubmit={submitLead} className="mt-10 rounded-3xl bg-blue-50/60 border border-blue-100 p-6 sm:p-8">
          <p className="font-display text-lg font-bold text-slate-900">¿Quieres recibir tu reporte detallado por email?</p>
          <p className="mt-1 text-sm text-slate-600">Te enviamos el resumen + tips ergonómicos para tu puesto + recordatorio del cupón.</p>
          <div className="mt-5 grid sm:grid-cols-2 gap-3">
            <input required value={name} onChange={(e) => setName(e.target.value)} placeholder="Tu nombre" className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none" />
            <input required type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="Tu correo" className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none" />
            <input value={phone} onChange={(e) => setPhone(e.target.value)} placeholder="Tu WhatsApp (opcional)" className="rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm focus:border-blue-500 focus:ring-2 focus:ring-blue-100 outline-none sm:col-span-2" />
          </div>
          {err && <p className="mt-3 text-sm text-rose-600">{err}</p>}
          <div className="mt-4 flex items-center justify-between gap-4">
            <p className="text-xs text-slate-500 inline-flex items-center gap-1.5"><Lock className="h-3.5 w-3.5" /> Tus datos están seguros.</p>
            <button type="submit" disabled={saving} className="inline-flex items-center gap-2 rounded-full bg-blue-600 hover:bg-blue-700 px-6 py-3 text-sm font-bold text-white disabled:opacity-60">{saving ? "Enviando…" : "Recibir reporte"}{!saving && <Send className="h-4 w-4" />}</button>
          </div>
        </form>
      )}
      {saved && (
        <div className="mt-10 rounded-3xl bg-blue-600 text-white p-6 text-center">
          <Check className="h-10 w-10 mx-auto" />
          <p className="mt-3 font-display text-xl font-bold">¡Listo! Tu reporte está en camino.</p>
        </div>
      )}

      <div className="mt-10 rounded-3xl bg-gradient-to-br from-blue-50 via-indigo-50 to-white border border-blue-100 p-7 sm:p-9 text-center">
        <div className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-blue-600 text-white shadow-lg shadow-blue-200">
          <Check className="h-7 w-7" />
        </div>
        <p className="mt-5 font-display text-xl sm:text-2xl font-bold text-slate-900">Agradecemos tu visita</p>
        <p className="mt-2 text-base text-slate-600 leading-relaxed max-w-xl mx-auto italic">Será un placer conocerte y servirte profesionalmente en nuestro consultorio.</p>
        <p className="mt-4 text-xs uppercase tracking-[0.25em] font-semibold text-blue-700">TerafiQuiro Wellness Center · Al cuidado de tu cuerpo</p>
      </div>

      <div className="mt-8 text-center">
        <button onClick={onRestart} className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-900"><RefreshCw className="h-4 w-4" /> Hacer el diagnóstico de nuevo</button>
      </div>
    </section>
  );
};

const STEP = { intro: "intro", quiz: "quiz", result: "result" };

const QuizPostural = () => {
  const [step, setStep] = useState(STEP.intro);
  const [answers, setAnswers] = useState(Array(QUIZPOST_QUESTIONS.length).fill(null));
  const [idx, setIdx] = useState(0);
  const ranked = useMemo(() => scoreConditionsPost(answers), [answers]);
  const severity = useMemo(() => overallSeverityPost(ranked), [ranked]);

  useEffect(() => { if (step === STEP.result) window.scrollTo({ top: 0, behavior: "smooth" }); }, [step]);

  const start = () => { setStep(STEP.quiz); setIdx(0); setAnswers(Array(QUIZPOST_QUESTIONS.length).fill(null)); };
  const handleAnswer = (val) => {
    const next = [...answers]; next[idx] = val; setAnswers(next);
    setTimeout(() => {
      if (idx + 1 < QUIZPOST_QUESTIONS.length) setIdx(idx + 1);
      else setStep(STEP.result);
    }, 250);
  };
  const restart = () => { setStep(STEP.intro); setAnswers(Array(QUIZPOST_QUESTIONS.length).fill(null)); setIdx(0); window.scrollTo({ top: 0, behavior: "smooth" }); };

  return (
    <main className="min-h-screen bg-gradient-to-b from-white to-blue-50/40 pt-10 sm:pt-12 pb-16">
      <style>{customAnimationsCSS}</style>
      {step === STEP.intro && <Intro onStart={start} />}
      {step === STEP.quiz && <Question q={QUIZPOST_QUESTIONS[idx]} idx={idx} total={QUIZPOST_QUESTIONS.length} onAnswer={handleAnswer} onBack={restart} />}
      {step === STEP.result && <Result ranked={ranked} severity={severity} answers={answers} onRestart={restart} />}
    </main>
  );
};

export default QuizPostural;
